import { emit } from '../core/utils.js';

let instance = 0;
export class TrackingAvatar extends HTMLElement {
  constructor() { super(); this.attachShadow({ mode: 'open' }); this.uid = ++instance; this.level = 0; }
  connectedCallback() {
    const id = this.uid;
    this.shadowRoot.innerHTML = `<style>
      :host{display:block;width:100%;height:100%;position:relative}svg{width:100%;height:100%;overflow:visible;pointer-events:none}
      .figure{transform-origin:94px 96px;transition:transform .12s ease-out,opacity .2s}.hand{display:none}
      .eye,.mouth{transform-box:fill-box;transform-origin:center;transition:transform .06s linear}
      .meter{color:var(--accent-strong,#709575)}.meter-bar{fill:currentColor;opacity:.14;transition:opacity .07s}
      .meter.active .meter-bar.lit{opacity:1}.mic{opacity:.4}.voice-enabled .mic{opacity:.9}.voice-enabled .mic-slash{display:none}
      .avatar-control{-webkit-tap-highlight-color:transparent;position:absolute;border:0;padding:0;background:transparent;color:var(--accent-strong,#709575);cursor:pointer;touch-action:manipulation}
      .camera-control{left:7%;top:12%;width:70%;height:75%;border-radius:35%}
      .mic-control{right:0;bottom:7%;width:24%;height:23%;min-width:32px;min-height:32px;border-radius:10px}
      .avatar-control:hover:not([data-touch-hover]){background:color-mix(in srgb,currentColor 9%,transparent)}
      .avatar-control:focus{outline:none}
      .avatar-control:focus-visible:not([data-pointer-focus]){outline:2px solid currentColor;outline-offset:2px}
      @media(max-width:900px){
        .meter-bar{x:164px;width:18px;height:6px;opacity:.28}
        .meter .mic{transform:translate(153px,141px) scale(1.5);opacity:.65}
        .mic-control{right:0;bottom:0;width:30%;height:30%;min-width:44px;min-height:44px}
      }
      @media(prefers-reduced-motion:reduce){*{transition:none!important}}
    </style><svg viewBox="0 0 200 190" aria-hidden="true">
      <defs>
        <linearGradient id="skin${id}" x1="0" y1="0" x2="1" y2="1"><stop stop-color="#f5e4ca"/><stop offset=".48" stop-color="#d9b98c"/><stop offset="1" stop-color="#907454"/></linearGradient>
        <linearGradient id="side${id}" x1="0" x2="1"><stop stop-color="#b89a73"/><stop offset="1" stop-color="#78634c"/></linearGradient>
        <radialGradient id="shine${id}" cx=".3" cy=".25" r=".8"><stop stop-color="#fff8e8" stop-opacity=".65"/><stop offset="1" stop-color="#fff" stop-opacity="0"/></radialGradient>
      </defs>
      <g class="figure face">
        <path d="M52 65 76 34 118 37 144 64 142 118 119 147 86 155 53 129 42 93Z" fill="url(#side${id})"/>
        <path d="m52 65 25-31 36 5 22 32-5 53-21 27-30-3-27-31-10-24Z" fill="url(#skin${id})"/>
        <path d="m52 65 25-31 36 5 22 32-5 53-21 27-30-3-27-31-10-24Z" fill="url(#shine${id})"/>
        <ellipse class="eye left-eye" cx="73" cy="88" rx="6.3" ry="8.4" fill="#393a34"/>
        <ellipse class="eye right-eye" cx="110" cy="88" rx="6.3" ry="8.4" fill="#393a34"/>
        <ellipse class="mouth" cx="92" cy="116" rx="10.5" ry="3.4" fill="#655547"/>
      </g>
      <g class="figure hand" stroke-linejoin="round" stroke-linecap="round">
        <path class="palm" fill="url(#skin${id})" stroke="url(#skin${id})"/>
        ${[1,2,3,4,0].map(i => `<path class="finger-edge e${i}" fill="none" stroke="#9b805e"/><path class="finger f${i}" fill="none" stroke="url(#skin${id})"/>`).join('')}
      </g>
      <g class="meter">
        ${Array.from({ length: 10 }, (_, i) => `<rect class="meter-bar" x="171" y="${133-i*8}" width="9" height="5" rx="2"/>`).join('')}
        <g class="mic" transform="translate(167 146) scale(.7)" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10v2a7 7 0 0 0 14 0v-2m-7 9v3m-4 0h8"/><path class="mic-slash" d="m3 3 18 18"/></g>
      </g>
    </svg><button type="button" class="avatar-control camera-control" data-input="camera" aria-label="Turn camera on" aria-pressed="false" title="Turn camera on"></button><button type="button" class="avatar-control mic-control" data-input="voice" aria-label="Turn microphone on" aria-pressed="false" title="Turn microphone on"></button>`;
    this.face = this.shadowRoot.querySelector('.face'); this.hand = this.shadowRoot.querySelector('.hand');
    this.leftEye = this.shadowRoot.querySelector('.left-eye'); this.rightEye = this.shadowRoot.querySelector('.right-eye'); this.mouth = this.shadowRoot.querySelector('.mouth');
    this.meter = this.shadowRoot.querySelector('.meter'); this.bars = [...this.shadowRoot.querySelectorAll('.meter-bar')];
    this.shadowRoot.querySelectorAll('[data-input]').forEach(button => button.addEventListener('click', () => emit(this, 'command', { type: button.dataset.input })));
    this.update(null); this.audio(0, false);
    this.setInputState('camera', this.cameraOn || false); this.setInputState('voice', this.voiceOn || false);
  }
  setInputState(kind, on) {
    this[`${kind}On`] = on;
    const button = this.shadowRoot.querySelector(`[data-input="${kind}"]`);
    if (!button) return;
    const label = `Turn ${kind === 'voice' ? 'microphone' : 'camera'} ${on ? 'off' : 'on'}`;
    button.setAttribute('aria-pressed', String(on)); button.setAttribute('aria-label', label); button.title = label;
    if (kind === 'voice') this.shadowRoot.querySelector('svg').classList.toggle('voice-enabled', on);
  }
  audio(level, active) {
    this.level = active ? Math.max(0, Math.min(1, level)) : 0;
    if (!this.meter) return;
    this.meter.classList.toggle('active', active);
    this.bars.forEach((bar, i) => bar.classList.toggle('lit', this.level > (i + .3) / this.bars.length));
    this.animateMouth();
  }
  animateMouth() {
    if (this.mouth) this.mouth.style.transform = `scaleY(${1 + Math.max(this.tracking?.face?.mouthOpen || 0, this.level * .8) * 3.5})`;
  }
  update(tracking) {
    this.tracking = tracking;
    if (!this.face) return;
    const hand = tracking?.hand, face = tracking?.face;
    this.face.style.display = hand ? 'none' : '';
    this.hand.style.display = hand ? 'inline' : 'none';
    this.face.style.opacity = face ? '1' : '.65';
    if (hand?.landmarks?.length >= 21) {
      // Normalize camera size and aspect ratio, then build a full palm with a broad wrist.
      const raw = hand.landmarks.map(p => ({ x: -p.x * 4 / 3, y: p.y }));
      const minX = Math.min(...raw.map(p => p.x)), maxX = Math.max(...raw.map(p => p.x));
      const minY = Math.min(...raw.map(p => p.y)), maxY = Math.max(...raw.map(p => p.y));
      const scale = Math.min(117 / Math.max(.01, maxX - minX), 126 / Math.max(.01, maxY - minY));
      const offsetX = (0.5 - hand.landmarks[9].x) * 18, offsetY = (hand.landmarks[9].y - .5) * 14;
      const points = raw.map(p => ({ x: 91 + offsetX + (p.x - (minX + maxX) / 2) * scale, y: 95 + offsetY + (p.y - (minY + maxY) / 2) * scale }));
      const width = Math.hypot(points[5].x - points[17].x, points[5].y - points[17].y);
      const ux = (points[5].x - points[17].x) / (width || 1), uy = (points[5].y - points[17].y) / (width || 1);
      const wrist = points[0], left = { x: wrist.x + ux * width * .34, y: wrist.y + uy * width * .34 }, right = { x: wrist.x - ux * width * .34, y: wrist.y - uy * width * .34 };
      const xy = p => `${p.x.toFixed(2)},${p.y.toFixed(2)}`;
      const palm = this.hand.querySelector('.palm');
      palm.setAttribute('d', `M${xy(left)} Q${xy(points[1])} ${xy(points[5])} L${xy(points[9])} ${xy(points[13])} ${xy(points[17])} Q${xy({ x: (points[17].x + right.x)/2, y: (points[17].y + right.y)/2 })} ${xy(right)} Q${xy(wrist)} ${xy(left)}Z`);
      palm.setAttribute('stroke-width', Math.max(7, width * .2));
      for (let finger = 0; finger < 5; finger++) {
        const indices = finger === 0 ? [1, 2, 3, 4] : [finger * 4 + 1, finger * 4 + 2, finger * 4 + 3, finger * 4 + 4];
        const base = points[indices[0]];
        const joints = indices.map(i => ({ x: base.x + (points[i].x - base.x) * .9, y: base.y + (points[i].y - base.y) * .9 }));
        const d = `M${joints.map(xy).join(' L')}`, thickness = Math.max(7, width * (finger === 0 ? .25 : .21));
        const path = this.hand.querySelector(`.f${finger}`), edge = this.hand.querySelector(`.e${finger}`);
        path.setAttribute('d', d); edge.setAttribute('d', d);
        path.setAttribute('stroke-width', thickness); edge.setAttribute('stroke-width', thickness + 1.3);
      }
    } else {
      const x = face ? (0.5 - face.x) * 30 : 0, y = face ? (face.y - 0.5) * 20 : 0;
      this.face.style.transform = `perspective(350px) translate(${x}px,${y}px) rotateZ(${face ? -face.roll : 0}deg) rotateY(${face?.yaw || 0}deg) rotateX(${-(face?.pitch || 0)}deg)`;
      this.leftEye.style.transform = `scaleY(${Math.max(.1, 1 - (face?.blinkLeft || 0))})`;
      this.rightEye.style.transform = `scaleY(${Math.max(.1, 1 - (face?.blinkRight || 0))})`;
    }
    this.animateMouth();
  }
}
customElements.define('mt-tracking-avatar', TrackingAvatar);
